import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';

import useFetchWrapper, { HttpResponse } from '../../Helpers/useFetchWrapper';

import {
	ForgotPasswordData,
	Page,
	PageConfig,
	PasswordStrength,
	Role,
} from './ResetPassword.model';

const useLogin = () => {
	const [ page, setPage ] = useState<PageConfig | null>(null);
	const [passwordStrength, setPasswordStrength] = useState(PasswordStrength.Weak);
	const [role, setRole] = useState<Role | undefined>();
	const [invalidToken, setInvalidToken] = useState(false);

	const { post, put } = useFetchWrapper();
	const history = useHistory();
	const { pathname, search } = useLocation();
	const intl = useIntl();
	const params = new URLSearchParams(search); // Get query params

	const initPage = () => {
		if ( pathname === Page.SetPassword ) {
			setPage({
				info: invalidToken ? intl.formatMessage({ id: 'setPassword.invalidToken.info' }): '',
				name: Page.SetPassword,
				showConsentCheckboxes: true,
				subtitle: invalidToken ? intl.formatMessage({ id: `setPassword.invalidToken.title` }) : intl.formatMessage({ id: 'setPassword.subtitle' }),
				title: intl.formatMessage({ id: 'setPassword.title' }),
			})
		} else {
			setPage({
				info: invalidToken ? intl.formatMessage({ id: 'resetPassword.invalidToken.info' }): '',
				name: Page.ResetPassword,
				showConsentCheckboxes: false,
				subtitle: invalidToken ? intl.formatMessage({ id: `resetPassword.invalidToken.title` }) : intl.formatMessage({ id: 'resetPassword.subtitle' }),
				title: intl.formatMessage({ id: `resetPassword.title` }),
			})
		}
	}

	const navigateTo = (path: string) => {
		history.push(path);
	}

	const onPasswordChange = (event: any) => {
		const { value } = event.currentTarget;

		if (/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})/.test(value)) {
			setPasswordStrength(PasswordStrength.Strong);
		}	else if (/(?=.*[a-z])(?=.*[0-9])(?=.{8,})/.test(value)) {
			setPasswordStrength(PasswordStrength.Medium);
		} else {
			setPasswordStrength(PasswordStrength.Weak);
		}
	}

	const onSubmit = async (data: ForgotPasswordData) => {
		const { password: newPassword } = data;
		const token = params.get('token');
		
		if (token) {
			const res: HttpResponse<any> = await post('/user/reset-password', { newPassword, token: decodeURI(token), }, true);

			if (res.ok) {
				navigateTo('/');
			}
		}
	}

	const validateToken = async () => {
		const token = params.get('token');
		const response: HttpResponse<Role> = await put('/user/validate-token', token);
		if (response.parsedBody && response.status === 200) setRole(response.parsedBody);
		else setInvalidToken(true);
	}

	useEffect(() => {
		validateToken();
	}, []);

	useEffect(() => {
		initPage();
	}, [invalidToken])

	return {
		intl,
		navigateTo,
		onPasswordChange,
		onSubmit,
		state: {
			invalidToken,
			page,
			passwordStrength,
			role
		},
	}
}

export default useLogin;